import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faEye } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "../screenstyles/Product.css";

interface UnCompleteOrderUser {
  userId: string;
  fullName: string;
  email: string;
  accountType: string;
  accountLevel: string;
  createdAt: any;
  userImage: string;
  totalItems: number;
  totalQuantity: number;
  totalPrice: number;

  addressLocationId: string;
  province: string;
  city: string;
  gate: string;
  locationName: string;
  deliveryPhone: string;
  addressName: string;
}

const extraStyles: { [key: string]: React.CSSProperties } = {
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "260px",
  },
  userImage: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #e2e8f0",
    flexShrink: 0,
  },
  fallbackAvatar: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#e2e8f0",
    color: "#475569",
    fontWeight: 700,
    border: "2px solid #e2e8f0",
    flexShrink: 0,
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "right",
    gap: "4px",
  },
  userName: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.3,
  },
  userEmail: {
    fontSize: "12px",
    color: "#64748b",
    lineHeight: 1.3,
  },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#eef6f8",
    color: "#3D7585",
    fontSize: "12px",
    fontWeight: 700,
  },
  addressCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    textAlign: "right",
    minWidth: "220px",
  },
  addressMain: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.4,
  },
  addressSub: {
    fontSize: "12px",
    color: "#64748b",
    lineHeight: 1.4,
    whiteSpace: "normal",
  },
};

const formatDate = (value: any) => {
  if (!value) return "غير متوفر";

  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    return date.toLocaleString("ar-IQ");
  } catch {
    return "غير متوفر";
  }
};

const formatCurrency = (amount: number) => {
  return `${amount.toLocaleString("en-US")} دينار`;
};

const UnCompleteOrdersList: React.FC = () => {
  const [orders, setOrders] = useState<UnCompleteOrderUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const navigate = useNavigate();

  const fetchUnCompleteOrders = async () => {
    setLoading(true);

    try {
      const ordersCollection = collection(db, "Order");
      const ordersDocs = await getDocs(ordersCollection);

      const fetchedOrders: UnCompleteOrderUser[] = [];

      for (const orderDoc of ordersDocs.docs) {
        const userId = orderDoc.id;
        const orderData = orderDoc.data();

        const unCompleteOrderCollection = collection(
          db,
          "Order",
          userId,
          "UnCompleteOrder"
        );

        const unCompleteOrderDocs = await getDocs(unCompleteOrderCollection);

        if (unCompleteOrderDocs.empty) continue;

        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        let fullName = "غير معروف";
        let email = "غير متوفر";
        let accountType = "غير متوفر";
        let accountLevel = "غير متوفر";
        let createdAt: any = null;
        let userImage = "";

        let addressLocationId = "";
        let province = "غير متوفر";
        let city = "غير متوفر";
        let gate = "غير متوفر";
        let locationName = "غير متوفر";
        let deliveryPhone = "غير متوفر";
        let addressName = "غير متوفر";

        if (userDoc.exists()) {
          const userData = userDoc.data();

          fullName = userData.fullName || userData.userName || "غير معروف";
          email = userData.email || "غير متوفر";
          accountType = userData.accountType || "غير متوفر";
          accountLevel =
            userData.AccountLevel || userData.accountLevel || "غير متوفر";
          createdAt = userData.createdAt || null;
          userImage =
            userData.profileImageUrl ||
            userData.photoURL ||
            userData.userImage ||
            userData.image ||
            "";

          addressLocationId =
            orderData?.AddressLocationId ||
            orderData?.addressLocationId ||
            "";

          const choosentechId = orderData?.choosentech || "";
          let addressData: any = null;

          if (addressLocationId) {
            const addressDocRef = doc(
              db,
              "users",
              userId,
              "addresses",
              addressLocationId
            );
            const addressDoc = await getDoc(addressDocRef);

            if (addressDoc.exists()) {
              addressData = addressDoc.data();
            }
          }

          if (!addressData && choosentechId) {
            const choosenTechAddressRef = doc(
              db,
              "users",
              userId,
              "addresses",
              choosentechId
            );
            const choosenTechAddressDoc = await getDoc(choosenTechAddressRef);

            if (choosenTechAddressDoc.exists()) {
              addressData = choosenTechAddressDoc.data();
              addressLocationId = choosentechId;
            }
          }

          if (!addressData) {
            const addressesRef = collection(db, "users", userId, "addresses");
            const addressesSnapshot = await getDocs(addressesRef);

            if (!addressesSnapshot.empty) {
              const firstAddressDoc = addressesSnapshot.docs[0];
              addressData = firstAddressDoc.data();
              addressLocationId = firstAddressDoc.id;
            }
          }

          if (addressData) {
            province = addressData.province || addressData.Province || "غير متوفر";
            city = addressData.City || addressData.city || "غير متوفر";
            gate = addressData.gate || addressData.street || "غير متوفر";
            locationName =
              addressData.locationName ||
              addressData.location?.locationName ||
              addressData.address ||
              "غير متوفر";
            deliveryPhone =
              addressData.phoneNumber ||
              addressData.phone ||
              userData.phoneNumber ||
              "غير متوفر";
            addressName =
              addressData.addressName || addressData.name || "غير متوفر";
          }
        }

        let totalItems = 0;
        let totalQuantity = 0;
        let totalPrice = 0;

        unCompleteOrderDocs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const quantity = Number(data.quantity || 0);
          const price = Number(data.price || 0);

          totalItems += 1;
          totalQuantity += quantity;
          totalPrice += price * quantity;
        });

        fetchedOrders.push({
          userId,
          fullName,
          email,
          accountType,
          accountLevel,
          createdAt,
          userImage,
          totalItems,
          totalQuantity,
          totalPrice,
          addressLocationId,
          province,
          city,
          gate,
          locationName,
          deliveryPhone,
          addressName,
        });
      }

      console.log("FINAL CURRENT fetchedOrders:", fetchedOrders);
      setOrders(fetchedOrders);
    } catch (error) {
      console.error("Error fetching uncomplete orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnCompleteOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return orders;

    return orders.filter(
      (order) =>
        order.fullName.toLowerCase().includes(query) ||
        order.email.toLowerCase().includes(query) ||
        order.accountType.toLowerCase().includes(query) ||
        order.accountLevel.toLowerCase().includes(query) ||
        order.province.toLowerCase().includes(query) ||
        order.city.toLowerCase().includes(query) ||
        order.gate.toLowerCase().includes(query)
    );
  }, [orders, searchQuery]);

  return (
    <div className="product-container">
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl flex-1"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          الــــطــــلــــبــــات غــــيــــر الــــمــــكــــتــــمــــلــــة
        </h1>

        <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-72 shadow-md ml-2">
          <FontAwesomeIcon icon={faSearch} className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="ابحث بالاسم أو الإيميل أو المدينة..."
            className="bg-transparent focus:outline-none w-full px-4 text-sm text-gray-700 py-1 font-custom"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p>جاري تحميل البيانات...</p>
      ) : (
        <div className="table-container">
          <table className="product-table">
            <thead className="color-table">
              <tr>
                <th>#</th>
                <th>العميل</th>
                <th>نوع الحساب</th>
                <th>مستوى الحساب</th>
                <th>تاريخ الإنشاء</th>
                <th>المحافظة / المدينة</th>
                <th>بيانات التوصيل</th>
                <th>عدد المواد</th>
                <th>إجمالي الكمية</th>
                <th>إجمالي السعر</th>
                <th>التفاصيل</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order, index) => {
                const safeImage = (order.userImage || "").trim();

                return (
                  <tr key={order.userId}>
                    <td>{index + 1}</td>

                    <td>
                      <div style={extraStyles.userCell}>
                        {safeImage ? (
                          <img
                            src={safeImage}
                            alt={order.fullName}
                            className="product-image"
                            style={extraStyles.userImage}
                            onError={(e) => {
                              console.log("Image failed:", order.userId, safeImage);
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div style={extraStyles.fallbackAvatar}>
                            {order.fullName?.charAt(0) || "؟"}
                          </div>
                        )}

                        <div style={extraStyles.userInfo}>
                          <span style={extraStyles.userName}>{order.fullName}</span>
                          <span style={extraStyles.userEmail}>{order.email}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span style={extraStyles.badge}>{order.accountType}</span>
                    </td>

                    <td>
                      <span style={extraStyles.badge}>{order.accountLevel}</span>
                    </td>

                    <td>{formatDate(order.createdAt)}</td>

                    <td>
                      <div style={extraStyles.addressCell}>
                        <span style={extraStyles.addressMain}>
                          {order.province} / {order.city}
                        </span>
                        <span style={extraStyles.addressSub}>
                          {order.addressName !== "غير متوفر" ? order.addressName : ""}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div style={extraStyles.addressCell}>
                        <span style={extraStyles.addressMain}>{order.gate}</span>
                        <span style={extraStyles.addressSub}>{order.locationName}</span>
                        <span style={extraStyles.addressSub}>{order.deliveryPhone}</span>
                      </div>
                    </td>

                    <td>{order.totalItems}</td>
                    <td>{order.totalQuantity}</td>
                    <td>{formatCurrency(order.totalPrice)}</td>

                    <td>
                      <div className="icon-container">
                        <button
                          className="icon-button"
                          onClick={() =>
                            navigate(`/dashboard/uncomplete-user-orders/${order.userId}`)
                          }
                          title="عرض الطلبات"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UnCompleteOrdersList;